import { useMemo, useRef, useState } from "react";
import { Namespace, useT } from "@racoon/i18n";
import { useVirtualizer } from "@tanstack/react-virtual";
import { CardContentWrapper } from "~/ui/components/cardContent.wrapper";
import { InfoRow } from "~/ui/components/infoRow";
import { ViewSelector } from "../../../shared/components/viewSelector";
import { DateView, getLastSevenDaysDates } from "./dateView";
import { StatCard } from "./statCard";
import {
  usePickupLocationAnalyticsByIdQuery,
  usePickupLocationByIdQuery,
} from "~/shared/api/govDash/govDash.api";
import { ConnectProto } from "@racoon/api";
import { twoGisLinkOf } from "@racoon/lib";
import {
  formatTimestampToDate,
  toLocalWallClockTimestamp,
} from "~/shared/utils/filterDateFormatter";

export const PickupLocationDetails = ({
  selectedRecordId,
}: {
  selectedRecordId: string | undefined;
}) => {
  const t = useT();
  const [activeSection, setActiveSection] = useState<"general" | "summary">("general");

  const { data: pickupLocationData } = usePickupLocationByIdQuery({
    id: selectedRecordId ? BigInt(selectedRecordId) : undefined,
  });

  const renderPickupLocationType = useMemo(() => {
    // The provider's own type when the server sends it; the deprecated enum otherwise.
    const customType = pickupLocationData?.pickupLocation?.pickupLocationType?.name;
    if (customType) return customType;
    const type = pickupLocationData?.pickupLocation?.type;
    const typeName = typeof type === "string" ? type : ConnectProto.PickupLocationType[type ?? 0];

    if (
      type === ConnectProto.PickupLocationType.CONSTRUCTION ||
      typeName === "CONSTRUCTION" ||
      typeName === "PICKUP_LOCATION_TYPE_CONSTRUCTION"
    ) {
      return t(Namespace.Govdash, "pickupLocationType.construction");
    }
    if (
      type === ConnectProto.PickupLocationType.HOUSEHOLD ||
      typeName === "HOUSEHOLD" ||
      typeName === "PICKUP_LOCATION_TYPE_HOUSEHOLD"
    ) {
      return t(Namespace.Govdash, "pickupLocationType.household");
    }
    return "-";
  }, [pickupLocationData, t]);

  const viewOptions = useMemo(
    () =>
      [
        { value: "general" as const, label: t(Namespace.Govdash, "general") },
        { value: "summary" as const, label: t(Namespace.Govdash, "summary") },
      ] as const,
    [t],
  );

  const pickupLocation = pickupLocationData?.pickupLocation;
  const gisLink = pickupLocation ? twoGisLinkOf(pickupLocation) : null;

  const jurRows = useMemo(
    () => [
      {
        label: t(Namespace.Govdash, "pickupLocationId"),
        value:
          pickupLocationData?.pickupLocation?.id != null
            ? `PL${pickupLocationData.pickupLocation.id}`
            : "-",
      },
      { label: t(Namespace.Govdash, "pickupLocationType"), value: renderPickupLocationType },
      // {
      //   label: t(Namespace.Govdash, "jurAddress"),
      //   value: "Republic of Kazakhstan, Almaty, Kabanbay Batyr 453",
      // },
      {
        label: t(Namespace.Govdash, "geolocation"),
        value: gisLink ? (
          <a
            className="block max-w-xs min-w-20 truncate"
            href={gisLink}
            target="_blank"
            rel="noreferrer"
          >
            {gisLink}
          </a>
        ) : (
          "-"
        ),
      },
      // TODO: implement once back-end provides
      // { label: t(Namespace.Govdash, "district"), value: "Bostandyk district" },
      {
        label: t(Namespace.Govdash, "servicingMvo"),
        value: pickupLocationData?.serviceProvider?.name || "-",
      },
      // TODO: implement once back-end provides
      // { label: t(Namespace.Govdash, "lastVisit"), value: "Today 05:43" },
    ],
    [t, pickupLocationData, gisLink],
  );

  const containerGroupSections = useMemo(
    () =>
      pickupLocationData?.containerGroups?.map((group) => ({
        key: group.id,
        rows: [
          {
            label: t(Namespace.Govdash, "containerGroup"),
            value: group.containerType?.name
              ? `${group.containerType.name} (${group.numberOfContainers})`
              : "-",
          },
          { label: t(Namespace.Govdash, "wasteType"), value: t(Namespace.Govdash, "tabs.tbo") },
          {
            label: t(Namespace.Govdash, "nominalVolume"),
            value: `${group.containerType?.volume ?? 0} ${t(Namespace.Govdash, "volumeMetric")}`,
          },
          // { label: t(Namespace.Govdash, "collectionSchedule"), value: group.schedule },
          // { label: t(Namespace.Govdash, "wasteGenerator"), value: group.wasteGenerator },
          // { label: t(Namespace.Govdash, "contract"), value: group.contractName },
        ],
      })) || [],
    [t, pickupLocationData],
  );

  return (
    <CardContentWrapper
      hasDivider={activeSection === "summary"}
      className="flex min-h-0 flex-col overflow-hidden"
    >
      <div className="p-2">
        <ViewSelector
          options={viewOptions}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />
      </div>
      {activeSection === "general" && (
        <div className="overflow-y-auto min-h-0 flex-1">
          <div className="p-4 space-y-4">
            <p className="text-base font-semibold tracking-tight text-slate-900">
              {t(Namespace.Govdash, "juridicalInfo")}
            </p>
            <CardContentWrapper>
              {jurRows.map((row) => (
                <InfoRow key={row.label} label={row.label} value={row.value} />
              ))}
            </CardContentWrapper>
          </div>
          {containerGroupSections.length > 0 && (
            <div className="p-4 space-y-4">
              <p className="text-base font-semibold tracking-tight text-slate-900">
                {t(Namespace.Govdash, "containerGroups")}
              </p>
              <div className="space-y-3">
                {containerGroupSections.map((section, i) => (
                  <CardContentWrapper key={section.key}>
                    {section.rows.map((row) => (
                      <InfoRow
                        key={`${section.key}-${row.label}`}
                        label={
                          row.label === t(Namespace.Govdash, "containerGroup")
                            ? `${t(Namespace.Govdash, "containerGroup")} ${i + 1}`
                            : row.label
                        }
                        value={row.value}
                      />
                    ))}
                  </CardContentWrapper>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {activeSection === "summary" && (
        <div className="flex min-h-0 flex-1 flex-col">
          <PickupLocationSummary selectedRecordId={selectedRecordId} />
        </div>
      )}
    </CardContentWrapper>
  );
};

const PickupLocationSummary = ({ selectedRecordId }: { selectedRecordId: string | undefined }) => {
  const t = useT();
  const { weekStart: initialWeekStart, weekEnd: initialWeekEnd } = getLastSevenDaysDates();
  const [weekStart, setWeekStart] = useState(initialWeekStart);
  const [weekEnd, setWeekEnd] = useState(initialWeekEnd);
  const stopsListRef = useRef<HTMLDivElement | null>(null);
  const { data: pickupLocationAnalyticsData } = usePickupLocationAnalyticsByIdQuery(
    {
      pickupLocationId: selectedRecordId ? BigInt(selectedRecordId) : undefined,
      fromTo: {
        from: toLocalWallClockTimestamp(weekStart),
        to: toLocalWallClockTimestamp(weekEnd),
      },
    },
    {
      skip: !selectedRecordId,
    },
  );
  const gpsStops = pickupLocationAnalyticsData?.gpsStops ?? [];
  const gpsStopsVirtualizer = useVirtualizer({
    count: gpsStops.length,
    getScrollElement: () => stopsListRef.current,
    estimateSize: () => 52,
    overscan: 8,
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DateView
        weekStart={weekStart}
        weekEnd={weekEnd}
        onDateChange={(from, to) => {
          if (from) setWeekStart(from);
          if (to) setWeekEnd(to);
        }}
      />
      <div className="flex gap-2 items-center p-4">
        <StatCard
          title={t(Namespace.Govdash, "visits")}
          value={pickupLocationAnalyticsData?.totalLocationStops?.toString() ?? "0"}
        />
        {/* <StatCard title={t(Namespace.Govdash, "wasteVolume")} value={891} /> */}
      </div>
      <p className="text-base font-semibold tracking-tight text-slate-900 p-4">
        {t(Namespace.Govdash, "visitRecords")}
      </p>
      {gpsStops.length > 0 ? (
        <div ref={stopsListRef} className="min-h-0 flex-1 overflow-y-auto">
          <div
            role="list"
            className="relative w-full"
            style={{ height: `${gpsStopsVirtualizer.getTotalSize()}px` }}
          >
            {gpsStopsVirtualizer.getVirtualItems().map((virtualRow) => {
              const visit = gpsStops[virtualRow.index];
              return (
                <div
                  role="listitem"
                  aria-setsize={gpsStops.length}
                  aria-posinset={virtualRow.index + 1}
                  key={visit.id}
                  data-index={virtualRow.index}
                  ref={gpsStopsVirtualizer.measureElement}
                  className="absolute left-0 top-0 w-full"
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                >
                  <div className="flex gap-x-4 gap-y-1 border-b border-slate-200 p-4 text-xs align-center">
                    <span className="font-medium text-slate-900">#{visit.id.toString()}</span>
                    <div className="flex flex-1 gap-x-4 gap-y-1 items-center justify-between">
                      <span className="text-slate-700">{visit.truck?.plateNumber}</span>
                      {/* <span className="text-text-secondary">{visit.volume}</span> */}
                      <span className="text-text-secondary">
                        {formatTimestampToDate(visit.gpsStartAt)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 justify-center items-center">
          <p className="text-sm text-text-secondary">{t(Namespace.Trucks, "tableNoData")}</p>
        </div>
      )}
    </div>
  );
};
